import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  GraduationCap, HeartPulse, Users, Target, Boxes, ShoppingCart,
  Factory, Truck, Building2, Calculator, BedDouble, MoreHorizontal,
  LayoutGrid, Briefcase, Cloud, Cpu, ShieldCheck,
  ArrowRight, ChevronRight, Utensils,
} from 'lucide-react';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* ============================== DATA ============================== */

const STATS = [
  { Icon: LayoutGrid, top: '50+', bottom: 'Powerful Modules' },
  { Icon: ShieldCheck, top: 'Secure', bottom: '& Reliable' },
  { Icon: Cloud, top: 'Cloud', bottom: 'Ready' },
  { Icon: Cpu, top: 'AI', bottom: 'Powered' },
];

const CATEGORIES = [
  { label: 'All Products', Icon: LayoutGrid },
  { label: 'Education', Icon: GraduationCap },
  { label: 'Healthcare', Icon: HeartPulse },
  { label: 'Food Processing', Icon: Utensils },
  { label: 'Business', Icon: Briefcase },
  { label: 'Finance', Icon: Calculator },
  { label: 'Manufacturing', Icon: Factory },
  { label: 'Retail', Icon: ShoppingCart },
  { label: 'Hospitality', Icon: BedDouble },
  { label: 'Logistics', Icon: Truck },
];

const PRODUCTS = [
  {
    id: 'education-erp', Icon: GraduationCap, color: '#3284c7', title: 'Education ERP', category: 'Education',
    desc: 'Complete solution for schools, colleges, universities and coaching institutes.',
    features: ['Admission Management', 'Attendance', 'Examination', 'Fees & Finance', 'Transport', 'Library', 'Hostel', 'HR & Payroll'],
    link: '/Educationerp'
  },
  {
    id: 'hospital-erp', Icon: HeartPulse, color: '#16a34a', title: 'Hospital ERP', category: 'Healthcare',
    desc: 'End-to-end hospital management for clinics, hospitals and healthcare centers.',
    features: ['OPD & IPD', 'Patient Management', 'Pharmacy', 'Laboratory', 'Radiology', 'EMR', 'Billing & Insurance', 'Ambulance'],
    link: '/Hospitalerp'
  },
  {
    id: 'hrms', Icon: Users, color: '#9333ea', title: 'HRMS', category: 'Business',
    desc: 'Automate HR operations and empower your workforce efficiently.',
    features: ['Employee Management', 'Attendance', 'Payroll', 'Leave Management', 'Recruitment', 'Performance', 'Training'],
    link: '/HRMS'
  },
  {
    id: 'crm', Icon: Target, color: '#f29132', title: 'CRM', category: 'Business',
    desc: 'Manage leads, customers and sales pipeline in one powerful system.',
    features: ['Leads Management', 'Sales Automation', 'Customer Management', 'Marketing', 'Follow-ups', 'Reports & Analytics'],
    link: '/CRM'
  },
  {
    id: 'inventory-erp', Icon: Boxes, color: '#0d9488', title: 'Inventory ERP', category: 'Business',
    desc: 'Smart inventory & stock management for all types of businesses.',
    features: ['Stock Management', 'Purchase & Sales', 'Warehouse', 'Multi-location', 'Barcode / QR Code', 'Reports'],
    link: '/Inventoryerp'
  },
  {
    id: 'retail-pos', Icon: ShoppingCart, color: '#e11d48', title: 'Retail POS', category: 'Retail',
    desc: 'Advanced POS & billing solution for retail stores and chains.',
    features: ['Billing & Invoicing', 'Barcode Scanning', 'Customer Management', 'Offers & Discounts', 'Loyalty Program', 'Sales Reports'],
    link: '/Retailpos'
  },
  {
    id: 'manufacturing-erp', Icon: Factory, color: '#f59e0b', title: 'Manufacturing ERP', category: 'Manufacturing',
    desc: 'Streamline production, quality and supply chain operations.',
    features: ['Production Planning', 'Material Management', 'Quality Control', 'Maintenance', 'Costing', 'Inventory', 'Reports'],
    link: '/Manufacturingerp'
  },
  {
    id: 'logistics-erp', Icon: Truck, color: '#3284c7', title: 'Logistics ERP', category: 'Logistics',
    desc: 'Manage fleet, routes, deliveries and logistics operations efficiently.',
    features: ['Fleet Management', 'GPS Tracking', 'Route Planning', 'Transport Management', 'Delivery Management', 'Warehouse', 'Reports'],
    link: '/Logisticserp'
  },
  {
    id: 'Food-erp', Icon: Utensils, color: '#16a34a', title: 'Food Processing ERP', category: 'Food Processing',
    desc: 'Complete ERP for Food Processing companies and Food online developers.',
    features: ['Project Management', 'Inventory Management', 'Material Tracking', 'Food Management', 'Billing & Payments', 'Reports'],
    link: '/Fooderp'
  },
  {
    id: 'finance-erp', Icon: Calculator, color: '#0d9488', title: 'Finance ERP', category: 'Finance',
    desc: 'Accounting, GST, billing and finance management in one place.',
    features: ['Accounting', 'GST Management', 'Accounts Payable', 'Accounts Receivable', 'Budgeting', 'Financial Reports'],
    link: '/Financeerp'
  },
  {
    id: 'hotel-management', Icon: BedDouble, color: '#ec4899', title: 'Hotel Management', category: 'Hospitality',
    desc: 'Manage hotel operations, bookings and guest services seamlessly.',
    features: ['Room Management', 'Booking & Reservation', 'Housekeeping', 'Restaurant POS', 'Billing', 'Reports'],
    link: '/Hotelmanagement'
  },
  {
    id: 'many-more', Icon: MoreHorizontal, color: '#9333ea', title: 'And Many More', category: 'more',
    desc: 'Other ready-to-use solutions to grow your business smarter.',
    features: ['Pharmacy ERP', 'School Transport', 'Asset Management', 'Library Management', 'NGO Management', 'Custom ERP Solutions'],
    link: '/Customerp'
  },
];

/* ============================== HELPERS ============================== */

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ============================== HERO IMAGE ============================== */

const HERO_IMAGE = {
  desktopWidth: '750px',
  desktopHeight: 'auto',
  offsetX: '0px',
  offsetY: '0px',
};

const HeroArt = () => (
  <img
    src="/Product_Img.png"
    alt="SmartEdz products preview"
    className="se-hero-img w-full h-auto max-w-[520px] mx-auto block select-none drop-shadow-2xl"
    draggable="false"
    style={{
      '--hero-w': HERO_IMAGE.desktopWidth,
      '--hero-h': HERO_IMAGE.desktopHeight,
      '--hero-x': HERO_IMAGE.offsetX,
      '--hero-y': HERO_IMAGE.offsetY,
    }}
    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
  />
);

/* ============================== PAGE ============================== */

const Products = () => {
  const location = useLocation();
  const [active, setActive] = useState('All Products');

  // deep-link scroll
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-primary', 'rounded-3xl');
          setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'rounded-3xl'), 1600);
        }
      });
    }
  }, [location]);

  const visible = PRODUCTS.filter((p) => {
    if (p.category === 'more') return true;
    if (active === 'All Products') return true;
    return p.category === active;
  });

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes seFloatSlow { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .se-float { animation: seFloat 5.5s ease-in-out infinite; }
        .se-float-slow { animation: seFloatSlow 7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-float-slow { animation: none !important; } }
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 560px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-5">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Products</span>
              </nav>

              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>
                Our Products
              </h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                Innovative software solutions engineered to automate business operations, boost productivity, and fuel sustainable growth.
              </p>

              <div className="grid grid-cols-4 gap-3 mt-8 max-w-lg">
                {STATS.map((s) => (
                  <div key={s.bottom} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-2">
                      <s.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-sm leading-tight">{s.top}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{s.bottom}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden sm:block relative lg:min-h-[500px]">
              <HeroArt />
            </div>
          </div>
        </div>
      </section>

      {/* ================= FILTER BAR ================= */}
      <section className={`${CONTAINER} relative z-20 -mt-2`}>
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-lg px-3 py-3 flex items-center gap-1.5 overflow-x-auto">
          {CATEGORIES.map((c) => {
            const isActive = active === c.label;
            return (
              <button
                key={c.label}
                onClick={() => setActive(c.label)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  isActive ? 'bg-primary text-white shadow' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <c.Icon className="w-4 h-4" />
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* ================= PRODUCTS GRID ================= */}
      <section className={`${CONTAINER} py-12`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={i * 50}>
              <div
                id={p.id}
                className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${p.color}1A` }}
                >
                  <p.Icon className="w-6 h-6" style={{ color: p.color }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-2">{p.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed mb-4">{p.desc}</p>
                <ul className="space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-slate-500">
                      <span className="mt-[7px] w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.link}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
                >
                  Explore Product <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= CUSTOM SOLUTION CTA ================= */}
      <section className={`${CONTAINER} pb-16`}>
        <div className="bg-gradient-to-r from-[#eaf2fb] to-[#e6eefb] rounded-2xl ring-1 ring-primary/10 px-6 sm:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5 text-center md:text-left">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 flex items-center justify-center shrink-0">
              <Boxes className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-extrabold" style={{ color: '#13294b' }}>Need a Custom Solution?</h3>
              <p className="text-slate-500 text-sm mt-1">We build tailor-made software to match your unique business requirements.</p>
            </div>
          </div>
          <Link
            to="/request-demo"
            className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition whitespace-nowrap"
          >
            Request Custom Solution <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;